import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Zap,
  Shield,
  Brain,
  Target,
  Clock,
  TrendingUp,
  Users,
  Code2,
  Blocks,
  Play,
  Terminal,
  Webhook,
  Check,
  Headphones,
  Stethoscope,
  Scale,
  GraduationCap,
  ShoppingCart,
  ChevronRight,
  Lock,
  Gauge,
  Settings2,
  BookOpen,
  Key,
  BarChart3,
  Globe,
  Cpu,
  Database,
  Puzzle,
  Activity,
  DollarSign,
  Bell,
  Search,
  Download,
  Eye,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

const CODE_EXAMPLE_INGEST = `// Stream transcript segments to Shadow
const session = await shadow.sessions.create({
  taxonomy: "sales_discovery",  // or your custom taxonomy
  sources: ["src_battlecard_q1", "src_pricing_2026"],
});

// Send segments as they arrive — from any source
session.ingest({
  speaker: "prospect",
  text: "Your competitor claims 99.9% uptime.",
  timestamp_ms: 1710342000000,
});`;

const CODE_EXAMPLE_CONSUME = `// Real-time intelligence via SSE
const stream = shadow.sessions.stream(session.id);

stream.on("card", (card) => {
  // card.type     → "fact_check"
  // card.urgency  → "immediate"
  // card.agent    → "fact_surfacer"
  // card.content  → { verdict, response, sources }
  renderToAgentUI(card);
});

stream.on("moment", (moment) => {
  // moment.type   → "factual_claim"
  // moment.confidence → 0.94
  logToAnalytics(moment);
});`;

const MOMENT_TYPES = [
  { type: "factual_claim", color: "bg-blue-500", desc: "A verifiable statement is made" },
  { type: "challenge", color: "bg-amber-500", desc: "Pushback or disagreement surfaces" },
  { type: "question_opportunity", color: "bg-violet-500", desc: "A chance to probe deeper" },
  { type: "explanation_needed", color: "bg-emerald-500", desc: "A concept needs clarification" },
  { type: "negotiation_tension", color: "bg-rose-500", desc: "Stakes rise in a negotiation" },
  { type: "commitment_made", color: "bg-cyan-500", desc: "An action item is spoken" },
];

const VERTICALS = [
  {
    icon: Headphones,
    name: "Contact Centers",
    tag: "Fastest path to revenue",
    desc: "Real-time agent assist for support and sales calls. Surface knowledge base articles, compliance reminders, and upsell cues as the conversation unfolds.",
    integrations: "Zendesk, Intercom, Five9, Genesys",
    highlight: true,
  },
  {
    icon: Target,
    name: "Sales Platforms",
    desc: "Embed live intelligence into your dialer or CRM. Battlecard surfacing, competitor handling, and deal-closing prompts — all via API.",
    integrations: "Salesforce, HubSpot, Outreach, Gong",
  },
  {
    icon: Stethoscope,
    name: "Telehealth",
    desc: "Clinical decision support during patient consultations. Surface relevant protocols, drug interactions, and documentation prompts in real time.",
    integrations: "Epic, Cerner, Athenahealth",
  },
  {
    icon: Scale,
    name: "Legal Tech",
    desc: "Assist attorneys during depositions and client calls with instant case law references, clause lookups, and risk flags.",
    integrations: "Clio, LexisNexis, Westlaw",
  },
  {
    icon: GraduationCap,
    name: "EdTech",
    desc: "Power AI tutors that respond to what students actually say — not just what they type. Real-time pedagogical moment detection.",
    integrations: "Canvas, Moodle, custom LMS",
  },
  {
    icon: ShoppingCart,
    name: "Commerce",
    desc: "Intelligent assist for live shopping, concierge services, and high-touch B2B sales calls. Surface product specs, inventory, and pricing on demand.",
    integrations: "Shopify, Magento, custom POS",
  },
];

const ARCHITECTURE_LAYERS = [
  {
    label: "Ingest",
    desc: "Webhook or WebSocket. Send transcript segments from any source — telephony, browser, bot.",
    icon: Webhook,
  },
  {
    label: "Classify",
    desc: "Moment classifier runs on a sliding window. Configurable taxonomy — use ours or define your own.",
    icon: Brain,
  },
  {
    label: "Dispatch",
    desc: "Specialist agents activate based on moment type. Grounded in your uploaded sources via RAG.",
    icon: Zap,
  },
  {
    label: "Deliver",
    desc: "Intelligence cards streamed back via SSE or webhook. Sub-7s latency end to end.",
    icon: ArrowRight,
  },
];

export default function ConversationalIntelligencePage() {
  const [activeTab, setActiveTab] = useState("ingest");

  return (
    <div className="min-h-screen bg-white antialiased">
      {/* Nav */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">LABS</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#architecture" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Architecture</a>
            <a href="#customize" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Customize</a>
            <a href="#observability" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Observability</a>
            <a href="#pricing" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Pricing</a>
            <Link to="/demo" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Live Demo</Link>
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20the%20Shadow%20Conversational%20Intelligence%20API"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#0f172a] text-white px-5 py-2 text-sm rounded-lg hover:bg-[#1e293b] transition-all font-medium"
            >
              Get API Access
            </a>
          </div>
        </nav>
      </header>

      {/* ════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════ */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.08),transparent)]" />

        <motion.div
          className="max-w-4xl mx-auto text-center relative"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.div variants={fadeUp} custom={0}>
            <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 text-xs font-medium px-3.5 py-1.5 rounded-full mb-6">
              <Terminal className="w-3.5 h-3.5" />
              Conversational Intelligence API
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.08] text-gray-900 mb-6"
          >
            Add real-time intelligence
            <br />
            <span className="bg-gradient-to-r from-gray-900 via-gray-500 to-gray-900 bg-clip-text text-transparent">
              to any conversation.
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Stream in a transcript. Get back classified moments and actionable 
            intelligence cards — grounded in your data, delivered in real time.
            <span className="text-gray-900 font-medium"> One API for every conversation that matters.</span>
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20the%20Shadow%20Conversational%20Intelligence%20API"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#0f172a] text-white px-8 py-4 text-sm rounded-xl hover:bg-[#1e293b] transition-all font-medium flex items-center gap-2 shadow-lg shadow-gray-900/10 hover:shadow-xl hover:shadow-gray-900/20 hover:scale-[1.02]"
            >
              Request API Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <Link
              to="/demo"
              className="group text-gray-600 hover:text-gray-900 px-8 py-4 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Play className="w-4 h-4" />
              See the reference app
            </Link>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> SOC 2 ready</span>
            <span className="flex items-center gap-1.5"><Gauge className="w-3 h-3" /> Sub-7s latency</span>
            <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Platform agnostic</span>
            <span className="flex items-center gap-1.5"><Blocks className="w-3 h-3" /> Configurable taxonomy</span>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════
          CODE PREVIEW
      ════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
              Three lines to ingest. One stream to consume.
            </h2>
          </div>

          <div className="bg-[#0f172a] rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/20">
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveTab("ingest")}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "ingest"
                    ? "text-white bg-white/10"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                Ingest Transcript
              </button>
              <button
                onClick={() => setActiveTab("consume")}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "consume"
                    ? "text-white bg-white/10"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                Consume Intelligence
              </button>
            </div>
            <div className="p-6 sm:p-8 overflow-x-auto">
              <pre className="text-sm leading-relaxed font-mono">
                <code className="text-gray-300">
                  {activeTab === "ingest" ? CODE_EXAMPLE_INGEST : CODE_EXAMPLE_CONSUME}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          ARCHITECTURE PIPELINE
      ════════════════════════════════════════════ */}
      <section id="architecture" className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Architecture</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              Transcript in. Intelligence out.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              A four-stage pipeline that turns raw conversation into classified moments 
              and actionable intelligence — in under 7 seconds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {ARCHITECTURE_LAYERS.map((layer, i) => {
              const Icon = layer.icon;
              return (
                <motion.div
                  key={layer.label}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-mono text-gray-300 font-bold">{String(i + 1).padStart(2, "0")}</span>
                    <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{layer.label}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{layer.desc}</p>
                  {i < 3 && (
                    <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          MOMENT TAXONOMY
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs text-gray-400 uppercase tracking-widest mb-4">
                Configurable Taxonomy
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-5">
                Your conversations.<br />Your moment types.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-gray-500 text-base leading-relaxed mb-8">
                Shadow ships with a battle-tested taxonomy for sales and support conversations. 
                But every business is different. Define your own moment types, map them to your 
                own specialist agents, and tune classification thresholds — all via the API.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="space-y-4">
                {[
                  "Use our default taxonomy or define custom moment types",
                  "Map any moment type to any agent (or bring your own)",
                  "Set per-moment confidence thresholds and cooldowns",
                  "A/B test taxonomies across sessions",
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <p className="text-sm text-gray-600">{point}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
            >
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
                  <Settings2 className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider font-mono">moment_taxonomy.json</span>
                </div>
                <div className="space-y-3">
                  {MOMENT_TYPES.map((m) => (
                    <div key={m.type} className="flex items-center gap-3 group">
                      <span className={`w-2 h-2 rounded-full ${m.color} flex-shrink-0`} />
                      <code className="text-sm font-mono text-gray-800 font-medium">{m.type}</code>
                      <span className="flex-1 border-b border-dashed border-gray-200 mx-1" />
                      <span className="text-xs text-gray-400 text-right">{m.desc}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 pt-2 opacity-50">
                    <span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                    <code className="text-sm font-mono text-gray-400">your_custom_type</code>
                    <span className="flex-1 border-b border-dashed border-gray-200 mx-1" />
                    <span className="text-xs text-gray-300 text-right">You define this</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          SOURCE GROUNDING / RAG
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={0}
              className="order-2 lg:order-1"
            >
              <div className="bg-[#0f172a] rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-3 border-b border-white/10">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-xs font-mono text-gray-400">session_8f3k · live</span>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-xs text-gray-500 mb-1 font-mono">speaker: "customer"</p>
                    <p className="text-sm text-gray-300">"We had 3 outages last quarter. What's your SLA guarantee?"</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Brain className="w-3.5 h-3.5 text-violet-400" />
                    <span className="text-xs text-gray-500">
                      classified → <span className="text-violet-400 font-mono">factual_claim</span> + <span className="text-amber-400 font-mono">challenge</span>
                    </span>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                      <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider font-mono">fact_check · from sources</span>
                    </div>
                    <p className="text-sm text-gray-200 leading-snug">
                      "Per uploaded SLA doc: 99.95% uptime, backed by $10K/hr credit. Their 99.9% claim has no financial SLA."
                    </p>
                    <p className="text-[10px] text-gray-600 mt-2 font-mono">src: enterprise-sla-2026.pdf · p.3</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="order-1 lg:order-2"
            >
              <motion.p variants={fadeUp} className="text-xs text-gray-400 uppercase tracking-widest mb-4">
                Source Grounding
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-5">
                Intelligence grounded<br />in your data. Not ours.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-gray-500 text-base leading-relaxed mb-8">
                Upload documents via the Sources API — battlecards, SOPs, product specs, compliance 
                guides. Shadow indexes them with hybrid search and surfaces the exact right snippet 
                when the conversation calls for it. Zero hallucination by design.
              </motion.p>
              <motion.div variants={fadeUp} custom={3}>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <pre className="text-sm font-mono text-gray-600 leading-relaxed overflow-x-auto">
{`// Upload a source
await shadow.sources.upload({
  file: "battlecard-q1.pdf",
  tags: ["competitor", "pricing"],
});

// Intelligence now references it
// automatically when relevant`}
                  </pre>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          CUSTOMIZABILITY
      ════════════════════════════════════════════ */}
      <section id="customize" className="py-20 sm:py-28 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Full Control</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              Your stack. Your rules.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Shadow is opinionated out of the box — but every layer is swappable. 
              Bring your own LLM, plug in your own vector store, or define entirely custom agents.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* BYO LLM */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-30px" }}
              variants={fadeUp}
              custom={0}
              className="bg-white rounded-2xl border border-gray-100 p-7 hover:border-gray-200 hover:shadow-lg transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Cpu className="w-5 h-5 text-violet-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Bring Your Own LLM</h3>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Use Gemini (default), GPT-4o, Claude, Llama, or any OpenAI-compatible endpoint. 
                Swap models per agent — use a fast model for classification, a strong one for reasoning.
              </p>
              <div className="mt-auto bg-gray-50 rounded-xl p-4 border border-gray-100">
                <pre className="text-xs font-mono text-gray-600 leading-relaxed overflow-x-auto whitespace-pre">
{`{
  "agents": {
    "fact_checker": {
      "llm": "openai/gpt-4o"
    },
    "argument_coach": {
      "llm": "anthropic/claude-sonnet"
    },
    "default": {
      "llm": "gemini-2.5-flash"
    }
  }
}`}
                </pre>
              </div>
            </motion.div>

            {/* Configurable RAG */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-30px" }}
              variants={fadeUp}
              custom={1}
              className="bg-white rounded-2xl border border-gray-100 p-7 hover:border-gray-200 hover:shadow-lg transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <Database className="w-5 h-5 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Configurable RAG</h3>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Use Shadow's managed vector store or plug in your own — Pinecone, Weaviate, 
                Qdrant, pgvector. Control chunking strategy, embedding model, and retrieval parameters.
              </p>
              <div className="mt-auto bg-gray-50 rounded-xl p-4 border border-gray-100">
                <pre className="text-xs font-mono text-gray-600 leading-relaxed overflow-x-auto whitespace-pre">
{`{
  "rag": {
    "provider": "pinecone",
    "index": "prod-knowledge",
    "embedding": "text-embedding-3-large",
    "retrieval": {
      "strategy": "hybrid",
      "top_k": 5
    }
  }
}`}
                </pre>
              </div>
            </motion.div>

            {/* Custom Agents */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-30px" }}
              variants={fadeUp}
              custom={2}
              className="bg-white rounded-2xl border border-gray-100 p-7 hover:border-gray-200 hover:shadow-lg transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Puzzle className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Custom Agents</h3>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Define your own specialist agents with custom prompts, tool access, and 
                moment-type mappings. Extend the built-in six or replace them entirely.
              </p>
              <div className="mt-auto bg-gray-50 rounded-xl p-4 border border-gray-100">
                <pre className="text-xs font-mono text-gray-600 leading-relaxed overflow-x-auto whitespace-pre">
{`{
  "agents": {
    "compliance_checker": {
      "prompt": "Flag regulatory risks...",
      "triggers": [
        "commitment_made",
        "negotiation_tension"
      ],
      "tools": ["policy_db", "reg_api"]
    }
  }
}`}
                </pre>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          OBSERVABILITY
      ════════════════════════════════════════════ */}
      <section id="observability" className="py-20 sm:py-28 px-4 bg-[#0f172a]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs text-gray-500 uppercase tracking-widest mb-4">
                Observability
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-5">
                See everything.<br />Debug anything.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-gray-400 text-base leading-relaxed mb-8">
                Every moment classification, agent dispatch, and card delivery is logged and 
                queryable. Trace a single card back to the exact transcript segment that triggered 
                it — with full prompt inspection.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="space-y-5">
                {[
                  { icon: Activity, title: "Session Timeline", desc: "Every event on a real-time timeline — classifications, dispatches, cards, errors." },
                  { icon: Gauge, title: "Latency Percentiles", desc: "p50/p95/p99 for classification, agent execution, and end-to-end delivery." },
                  { icon: DollarSign, title: "Cost Attribution", desc: "Per-session breakdown: LLM tokens, grounding searches, RAG queries, total spend." },
                  { icon: Search, title: "Agent Traces", desc: "Full input/output for every agent invocation with prompt inspection." },
                  { icon: Bell, title: "Alerts & Webhooks", desc: "Real-time alerts on latency spikes, error rates, or budget thresholds." },
                  { icon: Download, title: "Export Anywhere", desc: "Stream events to Datadog, Grafana, or your own observability stack." },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div key={item.title} variants={fadeUp} custom={i + 3} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-gray-300" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-0.5">{item.title}</h4>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Mock Dashboard */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
            >
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-mono text-gray-400">session_8f3k</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] text-emerald-400 font-mono">live</span>
                  </div>
                </div>

                {/* Metric Cards Row */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: "Moments", value: "47", sub: "classified" },
                    { label: "Cards", value: "23", sub: "delivered" },
                    { label: "p95 Latency", value: "4.2s", sub: "end-to-end" },
                  ].map((m) => (
                    <div key={m.label} className="bg-white/5 rounded-lg p-3 border border-white/5">
                      <p className="text-lg font-bold text-white">{m.value}</p>
                      <p className="text-[10px] text-gray-500">{m.label}</p>
                    </div>
                  ))}
                </div>

                {/* Cost Breakdown */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/5 mb-5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 font-medium">Cost Attribution</p>
                  <div className="space-y-2">
                    {[
                      { label: "LLM Tokens", value: "$0.58", pct: 68 },
                      { label: "Grounding", value: "$0.21", pct: 25 },
                      { label: "RAG Queries", value: "$0.05", pct: 7 },
                    ].map((c) => (
                      <div key={c.label} className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-500 w-20">{c.label}</span>
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full" style={{ width: `${c.pct}%` }} />
                        </div>
                        <span className="text-xs font-mono text-gray-300 w-12 text-right">{c.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex justify-between">
                    <span className="text-[10px] text-gray-500">Session total</span>
                    <span className="text-sm font-bold text-white font-mono">$0.84</span>
                  </div>
                </div>

                {/* Mini Timeline */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 font-medium">Agent Trace</p>
                  <div className="space-y-2.5">
                    {[
                      { time: "14:32:07", event: "moment: factual_claim", color: "bg-blue-400" },
                      { time: "14:32:07", event: "dispatch: fact_surfacer", color: "bg-amber-400" },
                      { time: "14:32:08", event: "rag: enterprise-sla.pdf", color: "bg-cyan-400" },
                      { time: "14:32:11", event: "card: fact_check delivered", color: "bg-emerald-400" },
                    ].map((e, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-[10px] font-mono text-gray-600 w-16">{e.time}</span>
                        <div className={`w-1.5 h-1.5 rounded-full ${e.color} flex-shrink-0`} />
                        <span className="text-xs font-mono text-gray-400">{e.event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          VERTICALS
      ════════════════════════════════════════════ */}
      <section id="verticals" className="py-20 sm:py-28 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Verticals</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              One API. Every conversation.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Shadow is platform-agnostic and vertical-agnostic. Stream in a transcript from 
              any source — telephony, browser, bot — and get back intelligence tuned for your domain.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VERTICALS.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.name}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-30px" }}
                  variants={fadeUp}
                  custom={i}
                  className={`rounded-2xl p-7 transition-all duration-300 flex flex-col ${
                    v.highlight
                      ? "bg-[#0f172a] text-white border-2 border-[#0f172a] relative"
                      : "bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg"
                  }`}
                >
                  {v.tag && (
                    <span className="absolute -top-3 right-6 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      {v.tag}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      v.highlight ? "bg-white/10" : "bg-gray-100"
                    }`}>
                      <Icon className={`w-5 h-5 ${v.highlight ? "text-white" : "text-gray-700"}`} />
                    </div>
                    <h3 className={`font-semibold ${v.highlight ? "text-white" : "text-gray-900"}`}>{v.name}</h3>
                  </div>
                  <p className={`text-sm leading-relaxed mb-5 flex-1 ${v.highlight ? "text-gray-300" : "text-gray-500"}`}>
                    {v.desc}
                  </p>
                  <div className={`pt-4 border-t ${v.highlight ? "border-white/10" : "border-gray-100"}`}>
                    <p className={`text-[10px] uppercase tracking-wider font-medium mb-1 ${v.highlight ? "text-gray-500" : "text-gray-400"}`}>
                      Integrates with
                    </p>
                    <p className={`text-xs ${v.highlight ? "text-gray-400" : "text-gray-500"}`}>{v.integrations}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          WHY BUILD ON SHADOW
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Build vs. Buy</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              12 months of R&D.<br />One afternoon to integrate.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-gray-200 p-8 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">Build In-House</h3>
              <ul className="space-y-4">
                {[
                  "Build moment classification from scratch",
                  "Design and train specialist agent prompts",
                  "Implement RAG pipeline with hybrid search",
                  "Handle deduplication, cooldowns, budgets",
                  "Build SSE infrastructure for real-time delivery",
                  "3-6 months of ML/infra engineering",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-gray-400">
                    <span className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px]">—</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-gray-900 p-8 bg-white relative">
              <div className="absolute -top-3 left-6">
                <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">Shadow API</span>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">Integrate in a Day</h3>
              <ul className="space-y-4">
                {[
                  "Battle-tested moment classification out of the box",
                  "6 specialist agents, tuned across thousands of sessions",
                  "RAG with hybrid search and intelligent routing built in",
                  "Content dedup, budgets, and rate limiting handled for you",
                  "SSE streaming with sub-7s end-to-end latency",
                  "Ship this week, not next quarter",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-gray-700">
                    <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 text-emerald-600" />
                    </div>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          DEVELOPER EXPERIENCE
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-[#0f172a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-4">Developer Experience</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5">
              Built by developers.<br />
              <span className="text-gray-400">For developers.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BookOpen, title: "API Docs", desc: "OpenAPI spec, quickstart guides, and full reference docs." },
              { icon: Key, title: "API Keys", desc: "Scoped keys with per-key rate limits and usage tracking." },
              { icon: BarChart3, title: "Usage Dashboard", desc: "Real-time session counts, latency percentiles, and spend." },
              { icon: Code2, title: "SDKs", desc: "First-class TypeScript and Python SDKs. More coming." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
                >
                  <Icon className="w-6 h-6 text-gray-400 mb-4" />
                  <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          PRICING
      ════════════════════════════════════════════ */}
      <section id="pricing" className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-5">
              Bring your own infra. Pay less.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
              Shadow charges for orchestration intelligence — the moment classification, agent dispatch, 
              and delivery layer. Bring your own LLM and RAG to cut costs by up to 85%.
            </p>
          </div>

          {/* Tier explanation */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-10 bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden"
          >
            <div className="grid grid-cols-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100">
              <div className="p-4" />
              <div className="p-4 border-l border-gray-100">Managed</div>
              <div className="p-4 border-l border-gray-100">BYO LLM</div>
              <div className="p-4 border-l border-gray-100 bg-gray-100/50 font-semibold text-gray-600">BYO Everything</div>
            </div>
            {[
              { label: "Orchestration engine", managed: true, byoLlm: true, byoAll: true },
              { label: "Moment classification", managed: true, byoLlm: true, byoAll: true },
              { label: "Agent dispatch + dedup", managed: true, byoLlm: true, byoAll: true },
              { label: "SSE / webhook delivery", managed: true, byoLlm: true, byoAll: true },
              { label: "Observability dashboard", managed: true, byoLlm: true, byoAll: true },
              { label: "LLM inference", managed: true, byoLlm: false, byoAll: false },
              { label: "RAG / vector search", managed: true, byoLlm: true, byoAll: false },
              { label: "Google Search grounding", managed: true, byoLlm: false, byoAll: false },
            ].map((row) => (
              <div key={row.label} className="grid grid-cols-4 text-sm border-b border-gray-100 last:border-b-0">
                <div className="p-3 px-4 text-gray-600 font-medium">{row.label}</div>
                {[row.managed, row.byoLlm, row.byoAll].map((included, i) => (
                  <div key={i} className={`p-3 border-l border-gray-100 flex justify-center ${i === 2 ? "bg-gray-100/50" : ""}`}>
                    {included ? (
                      <span className="text-xs text-gray-400">Included</span>
                    ) : (
                      <span className="text-xs text-gray-300">You provide</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {/* Free */}
            <div className="rounded-2xl border border-gray-200 p-6 bg-white hover:shadow-lg transition-shadow flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Build</h3>
              <p className="text-xs text-gray-400 mb-5">Prototype free</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-3xl font-bold text-gray-900">Free</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {["100 min / month", "Managed LLM + RAG", "Default taxonomy", "2 sources"].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27d%20like%20to%20try%20the%20Shadow%20API%20free%20tier"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-center block"
              >
                Get Started
              </a>
            </div>

            {/* Managed */}
            <div className="rounded-2xl border border-gray-200 p-6 bg-white hover:shadow-lg transition-shadow flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Managed</h3>
              <p className="text-xs text-gray-400 mb-5">We handle everything</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">$0.07</span>
                <span className="text-gray-500 text-sm">/ min</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">~$4.20 / hr</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {["Unlimited minutes", "Shadow LLM + RAG", "Custom taxonomies", "Observability"].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Shadow%20API%20Managed%20plan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-center block"
              >
                Request Access
              </a>
            </div>

            {/* BYO LLM */}
            <div className="rounded-2xl border border-gray-200 p-6 bg-white hover:shadow-lg transition-shadow flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">BYO LLM</h3>
              <p className="text-xs text-gray-400 mb-5">Your models, our orchestration</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">$0.03</span>
                <span className="text-gray-500 text-sm">/ min</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">~$1.80 / hr</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {["Unlimited minutes", "Your LLM keys", "Shadow RAG included", "Custom agents"].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Shadow%20API%20BYO%20LLM%20plan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-center block"
              >
                Request Access
              </a>
            </div>

            {/* BYO Everything */}
            <div className="rounded-2xl border-2 border-gray-900 p-6 bg-white relative hover:shadow-xl transition-shadow flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  Best Value
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">BYO Everything</h3>
              <p className="text-xs text-gray-400 mb-5">Pure orchestration layer</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">$0.01</span>
                <span className="text-gray-500 text-sm">/ min</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">~$0.60 / hr</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {["Unlimited minutes", "Your LLM + RAG", "Full customization", "Observability + export"].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Shadow%20API%20BYO%20Everything%20plan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors text-center block"
              >
                Request Access
              </a>
            </div>
          </div>

          {/* Cost Calculator */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-12 bg-gray-50 rounded-2xl border border-gray-100 p-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">What does it cost for 1 agent, 5 hrs/day, 25 days/month?</h3>
            <p className="text-xs text-gray-400 text-center mb-8">7,500 conversation-minutes per month</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { tier: "Managed", rate: "$0.07/min", cost: "$525", desc: "Shadow handles LLM, RAG, and infra", tag: null },
                { tier: "BYO LLM", rate: "$0.03/min", cost: "$225", desc: "Your LLM keys, our orchestration + RAG", tag: "57% savings" },
                { tier: "BYO Everything", rate: "$0.01/min", cost: "$75", desc: "Your LLM + RAG, pure orchestration", tag: "85% savings" },
              ].map((p) => (
                <div key={p.tier} className="bg-white rounded-xl p-5 border border-gray-100 text-center relative">
                  {p.tag && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      {p.tag}
                    </span>
                  )}
                  <p className="text-sm font-semibold text-gray-900 mb-0.5">{p.tier}</p>
                  <p className="text-xs text-gray-400 mb-3">{p.rate}</p>
                  <p className="text-3xl font-bold text-gray-900">{p.cost}<span className="text-sm text-gray-400 font-normal">/mo</span></p>
                  <p className="text-xs text-gray-400 mt-2">{p.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-gray-400 mt-6">
              Gong charges $100-175/user/month for post-meeting recording. Shadow delivers real-time intelligence during the conversation — starting at $75/month with BYO.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          REFERENCE APP CALLOUT
      ════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="flex flex-col md:flex-row items-center gap-8 md:gap-12"
          >
            <div className="flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Reference Implementation</p>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3">
                See it in action — live.
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Shadow's own meeting copilot is built entirely on this API. Try the interactive 
                demo to see moment classification, agent dispatch, and source grounding working 
                in a live simulated conversation.
              </p>
              <Link
                to="/demo"
                className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-6 py-3 text-sm rounded-lg hover:bg-[#1e293b] transition-all font-medium hover:scale-[1.02]"
              >
                <Play className="w-4 h-4" />
                Try the live demo
              </Link>
            </div>
            <div className="flex-shrink-0 w-full md:w-80 h-48 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <div className="text-center">
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-gray-700" />
                </div>
                <p className="text-sm font-medium text-gray-600">Shadow Meeting Copilot</p>
                <p className="text-xs text-gray-400">Built on this API</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          FINAL CTA
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
            Every conversation<br />is a data stream.
          </h2>
          <p className="text-xl text-gray-500 max-w-xl mx-auto mb-4 leading-relaxed">
            Start building on it.
          </p>
          <p className="text-gray-400 text-sm max-w-lg mx-auto mb-10 leading-relaxed">
            Get API access today. Ship real-time conversational intelligence 
            into your product this week — not next quarter.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20the%20Shadow%20Conversational%20Intelligence%20API"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#0f172a] text-white px-8 py-4 text-sm rounded-xl hover:bg-[#1e293b] transition-all font-medium flex items-center gap-2 shadow-lg shadow-gray-900/10 hover:shadow-xl hover:scale-[1.02]"
            >
              Request API Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <Link
              to="/demo"
              className="text-gray-600 hover:text-gray-900 px-8 py-4 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Play className="w-4 h-4" />
              See the reference app
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-black rounded-full" />
            <span className="text-sm font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">LABS</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <Link to="/demo" className="hover:text-gray-900 transition-colors">Demo</Link>
            <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-gray-400">&copy; 2026 ShadowLabs</p>
        </div>
      </footer>
    </div>
  );
}
